import { PrecisionManufacturingOutlined, QrCodeScannerOutlined } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { loadHandlingEquipment, returnHandlingEquipmentToPark } from "./api";
import { PageHeader } from "./PageHeader";
import { ScanInput } from "./ScanInput";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { setDiagnosticContext } from "./terminalDiagnostics";
import { formatMeasurement } from "./measurements";
import { useMeasurementSettings } from "./useMeasurementSettings";

export function HandlingEquipmentPage({
  onBack, permissions
}: { onBack: () => void; permissions: string[] }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage?.startsWith("en") ? "en-GB" : "fr-FR";
  const { configuration: measurements } = useMeasurementSettings();
  const queryClient = useQueryClient();
  const [equipmentNumber, setEquipmentNumber] = useState("");
  const [returnedNumber, setReturnedNumber] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string | null>(null);
  const [scanOpen, setScanOpen] = useState(false);
  const equipment = useQuery({
    queryKey: ["handling-equipment"], queryFn: loadHandlingEquipment
  });
  const canReturn = ["delivery:execute", "picking:execute", "putaway_task:execute"]
    .some((permission) => permissions.includes(permission));
  const returnToPark = useMutation({
    mutationFn: (identifier: string) => returnHandlingEquipmentToPark(identifier),
    onSuccess: async (returned) => {
      setReturnedNumber(returned.identifier);
      setEquipmentNumber("");
      await queryClient.invalidateQueries({ queryKey: ["handling-equipment"] });
    }
  });
  const sortedEquipment = useMemo(() => [...(equipment.data ?? [])].sort(
    (left, right) => left.identifier.localeCompare(right.identifier, locale, {
      numeric: true, sensitivity: "base"
    })
  ), [equipment.data, locale]);
  const available = sortedEquipment.filter((item) => item.status === "AVAILABLE").length;
  const engaged = sortedEquipment.filter((item) =>
    item.status === "ASSIGNED" || item.status === "IN_USE"
  ).length;
  const normalizedSearch = search.trim().toLocaleLowerCase(locale);
  const filteredEquipment = normalizedSearch ? sortedEquipment.filter((item) => [
    item.identifier, t(`handlingEquipment.types.${item.typeCode}`),
    t(`handlingEquipment.statuses.${item.status}`),
    item.warehouseCode, item.missionReference, item.handlingUnitNumbers,
    item.assignedUserDisplayName
  ].some((value) => (value ?? "").toLocaleLowerCase(locale)
    .includes(normalizedSearch))) : sortedEquipment;
  const selectedEquipment = filteredEquipment.find((item) =>
    item.id === selectedEquipmentId) ?? null;
  const selectedReleasable = selectedEquipment?.status === "ASSIGNED"
    || selectedEquipment?.status === "IN_USE";
  useEffect(() => {
    setDiagnosticContext({
      screen: t("handlingEquipment.module"),
      action: scanOpen || equipmentNumber
        ? t("handlingEquipment.diagnostics.return")
        : t("handlingEquipment.diagnostics.view"),
      entities: [
        ...(selectedEquipment ? [{ type: "EQUIPMENT" as const,
          identifier: selectedEquipment.identifier,
          status: selectedEquipment.status }] : []),
        ...(selectedEquipment?.missionReference ? [{ type: "MISSION" as const,
          identifier: selectedEquipment.missionReference }] : []),
        ...(selectedEquipment?.handlingUnitNumbers ?? "").split(/[,;\s]+/)
          .filter(Boolean).slice(0, 10).map((identifier) => ({
            type: "HU" as const, identifier
          }))
      ]
    });
  }, [selectedEquipment, scanOpen, equipmentNumber, t]);
  const submit = async (value: string) => {
    setReturnedNumber(null);
    await returnToPark.mutateAsync(value.toUpperCase());
  };
  const release = (identifier: string) => {
    setReturnedNumber(null);
    setEquipmentNumber(identifier);
    returnToPark.mutate(identifier);
  };

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" }, minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("handlingEquipment.moduleShort")}
        search={search} onSearchChange={setSearch}
        searchPlaceholder={t("handlingEquipment.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("handlingEquipment.module")} compact onBack={onBack} />
      </Box>
      <Card variant="outlined" sx={{ flexShrink: 0,
        display: { xs: "none", sm: "block" } }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}
              alignItems={{ md: "center" }} justifyContent="space-between">
              <Box>
                <Typography variant="h6">{t("handlingEquipment.parkReturn")}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t("handlingEquipment.parkReturnHelp")}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip icon={<PrecisionManufacturingOutlined />} color="success"
                  label={t("handlingEquipment.counts.available", { count: available })} />
                <Chip color={engaged ? "warning" : "default"}
                  label={t("handlingEquipment.counts.engaged", { count: engaged })} />
              </Stack>
            </Stack>
            {canReturn ? (
              <ScanInput label={t("handlingEquipment.scan.return")}
                placeholder={t("handlingEquipment.scan.placeholder")} value={equipmentNumber}
                onChange={(value) => setEquipmentNumber(value.toUpperCase())}
                onSubmit={submit} submitting={returnToPark.isPending}
                submitLabel={t("handlingEquipment.actions.releaseEquipment")} />
            ) : (
              <Alert severity="info">
                {t("handlingEquipment.permissions.readOnly")}
              </Alert>
            )}
            {returnedNumber && (
              <Alert severity="success">{t("handlingEquipment.success.available", { identifier: returnedNumber })}</Alert>
            )}
            {returnToPark.isError && (
              <Alert severity="error">
                {returnToPark.error instanceof Error
                  ? returnToPark.error.message : t("handlingEquipment.errors.release")}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
      <Stack direction="row" spacing={1} sx={{
        display: { xs: "flex", sm: "none" }, flexShrink: 0, px: .5
      }}>
        <Chip icon={<PrecisionManufacturingOutlined />} color="success"
          label={t("handlingEquipment.counts.available", { count: available })} />
        <Chip color={engaged ? "warning" : "default"}
          label={t("handlingEquipment.counts.engaged", { count: engaged })} />
      </Stack>
      <Card variant="outlined" sx={{
        flex: { xs: "1 1 0", sm: "none", md: 1 }, minHeight: 0,
        display: "flex", flexDirection: "column"
      }}>
        <CardContent sx={{
          flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: 1.5
        }}>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>{t("handlingEquipment.state")}</Typography>
          {equipment.isError && (
            <Alert severity="error">{t("handlingEquipment.errors.load")}</Alert>
          )}
          <Stack spacing={1} sx={{
            overflowY: { xs: "auto", sm: "visible", md: "auto" },
            minHeight: 0, pr: .5, pb: { xs: 10, md: 0 }
          }}>
            {filteredEquipment.map((item) => {
              const releasable = item.status === "ASSIGNED" || item.status === "IN_USE";
              return (
                <Box key={item.id} sx={{
                  position: "relative",
                  display: "grid", gap: 1.5, alignItems: "center",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr auto auto" },
                  border: 1, borderColor: "divider", borderRadius: 1, p: 1.5,
                  cursor: { xs: "pointer", sm: "default" },
                  outline: { xs: selectedEquipmentId === item.id
                    ? "2px solid" : "none", sm: "none" },
                  outlineColor: "primary.main"
                }} onClick={(event) => {
                  if ((event.target as HTMLElement).closest("button")) return;
                  setSelectedEquipmentId(item.id);
                }}>
                  <PrecisionManufacturingOutlined color="secondary"
                    sx={{ display: { xs: "block", sm: "none" } }} />
                  <Box>
                    <Typography fontWeight={800}>{item.identifier}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("handlingEquipment.labels.capacityWarehouse", {
                        type: t(`handlingEquipment.types.${item.typeCode}`),
                        capacity: formatMeasurement(item.maxLoadKg, "MASS",
                          measurements, locale),
                        warehouse: item.warehouseCode
                      })}
                    </Typography>
                    {item.missionReference && item.activityType && (
                      <Typography variant="body2" sx={{ mt: .5 }}>
                        <strong>{t(`handlingEquipment.activities.${item.activityType}`)} · {item.missionReference}</strong>
                        {item.handlingUnitNumbers
                          ? ` · ${t("handlingEquipment.labels.handlingUnits", { numbers: item.handlingUnitNumbers })}` : ""}
                        {item.assignedUserDisplayName
                          ? ` · ${item.assignedUserDisplayName}`
                          : ` · ${t("handlingEquipment.labels.notTaken")}`}
                      </Typography>
                    )}
                  </Box>
                  <Chip label={t(`handlingEquipment.statuses.${item.status}`)}
                    sx={{
                      position: { xs: "absolute", sm: "static" },
                      top: { xs: 12, sm: "auto" },
                      right: { xs: 12, sm: "auto" },
                      maxWidth: { xs: "calc(100% - 64px)", sm: "none" },
                      justifySelf: { sm: "auto" }
                    }}
                    color={item.status === "AVAILABLE" ? "success"
                      : releasable ? "warning" : "default"} />
                  {canReturn && releasable ? (
                    <Button variant="outlined" disabled={returnToPark.isPending}
                      sx={{ display: { xs: "none", sm: "inline-flex" } }}
                      onClick={() => release(item.identifier)}>
                      {t("handlingEquipment.actions.release")}
                    </Button>
                  ) : <Box sx={{ width: { sm: 88 } }} />}
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
      <MobileActionBar secondaryIcon={<QrCodeScannerOutlined />}
        secondaryLabel={t("handlingEquipment.actions.scan")}
        secondaryDisplayLabel={t("handlingEquipment.actions.scanShort")}
        onSecondary={canReturn ? () => setScanOpen(true) : undefined}
        primaryLabel={selectedReleasable ? t("handlingEquipment.actions.releaseEquipment")
          : t("handlingEquipment.actions.select")}
        primaryDisabled={!canReturn || !selectedReleasable}
        onPrimary={selectedEquipment && selectedReleasable
          ? () => release(selectedEquipment.identifier) : undefined} />
      <Dialog open={scanOpen} onClose={() => setScanOpen(false)} fullWidth
        maxWidth="sm">
        <DialogTitle>{t("handlingEquipment.dialog.return")}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <ScanInput label={t("handlingEquipment.labels.identifier")}
              placeholder={t("handlingEquipment.scan.placeholder")} value={equipmentNumber}
              onChange={(value) => setEquipmentNumber(value.toUpperCase())}
              onSubmit={async (value) => {
                await submit(value);
                setScanOpen(false);
              }} submitting={returnToPark.isPending}
              submitLabel={t("handlingEquipment.actions.releaseEquipment")} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScanOpen(false)}>{t("common.actions.close")}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
